// ─────────────────────────────────────────────────────────────────────────────
// ink_layer.ts — Ink stroke capture and rendering
//
// Uses Perfect Freehand for organic stroke outlines.
// All rendering targets a single Canvas 2D context.
// Handles: pressure, tilt, eraser end, live stroke, committed strokes.
// ─────────────────────────────────────────────────────────────────────────────

import { getStroke } from 'perfect-freehand';
import { BRUSH_PROFILES, type StrokePoint, type InkStroke, type InkStyle, type BrushProfile } from '@shared/notebook_model';

// ── Wand fade entry — transient ink that dissolves after pen-up ───────────────

interface WandFade {
  stroke: InkStroke;
  /** 1 → 0 over WAND_FADE_MS. */
  alpha: number;
  startedAt: number;
}

const WAND_FADE_MS = 450;

// ── Lava fade entry — selection circle dissolving with red glow ────────────────

interface LavaFade {
  stroke: InkStroke;
  alpha: number;
  startedAt: number;
}

const LAVA_FADE_MS = 500;
const LAVA_COLOR = '#cc3030';

/** Tilt-aware ink: per-point pressure is multiplied by a small
 *  tilt-magnitude gain before being fed to perfect-freehand. A pen
 *  held vertical produces no boost; a pen tilted at 60°+ adds up to
 *  this fraction to the effective pressure (clamped to 1.0). Gives a
 *  fountain-pen feel — broader strokes when the pen leans. Stylus-
 *  only: tilt fields are typically 0 for mouse / generic pointers,
 *  so non-pen sources naturally pass through unmodified. */
const TILT_PRESSURE_GAIN = 0.55;
/** Tilt magnitude (in degrees) at which the gain is fully applied.
 *  Past this, tilt stops increasing the boost. Most pens cap their
 *  reported tilt around 60-70°, so 60 keeps the response linear in
 *  the comfortable hand-angle range. */
const TILT_FULL_GAIN_DEG = 60;

/** Apply tilt-aware width modulation to a raw pressure value. */
function tiltAwarePressure(p: { pressure: number; tiltX: number; tiltY: number }): number {
  const mag = Math.hypot(p.tiltX || 0, p.tiltY || 0);
  const ramp = Math.min(1, mag / TILT_FULL_GAIN_DEG);
  const gain = 1 + ramp * TILT_PRESSURE_GAIN;
  const pressure = p.pressure ?? 0.5;
  return Math.min(1, pressure * gain);
}

/** Initial-tail glow on a live stroke: the first few points of the
 *  stroke pulse warm gold-brown at pen-down and decay to the ink
 *  colour over INK_TAIL_COOL_MS. Only those starting points carry the
 *  glow — ink drawn after the cool-out window stays plain. Visible
 *  only on the live (in-progress) stroke; committed strokes carry no
 *  glow. Tuned for a "ink drying" feel: long enough to read, soft
 *  enough not to catch the eye. */
const INK_TAIL_COOL_MS = 350;
/** Number of head points to glow. Caps at the first few mm of ink so
 *  the effect stays anchored at where the pen first touched down. */
const INK_TAIL_HEAD_PTS = 8;

// ── Perfect Freehand options ──────────────────────────────────────────────────

function pfOptions(style: InkStyle, profile: BrushProfile, isLive = false, hasRealPressure = true) {
  return {
    size: style.size,
    thinning: profile.thinning,
    smoothing: profile.smoothing,
    streamline: profile.streamline,
    simulatePressure: !hasRealPressure,
    last: !isLive,
    start: { cap: true, taper: style.size * profile.startTaper },
    end:   { cap: true, taper: style.size * profile.endTaper },
  };
}

// ── SVG path helper ───────────────────────────────────────────────────────────

function svgPathFromOutline(outline: number[][]): Path2D {
  const path = new Path2D();
  if (outline.length < 3) { return path; }
  path.moveTo(outline[0][0], outline[0][1]);
  for (let i = 1; i < outline.length - 1; i++) {
    const mx = (outline[i][0] + outline[i + 1][0]) / 2;
    const my = (outline[i][1] + outline[i + 1][1]) / 2;
    path.quadraticCurveTo(outline[i][0], outline[i][1], mx, my);
  }
  path.closePath();
  return path;
}

// ── Brush abstraction (Batch 4) ──────────────────────────────────────────────
//
// The ink layer dispatches stroke rendering to one of several brushes
// based on the stroke's profile flags. Today there are three concrete
// brushes (solid, textured, wet); Batch 5 will add at least a kinematic-
// aware StylusBrush registered the same way.
//
// Brushes share a single args bundle so adding a new one is a one-liner
// in `_brushes` plus a registered render function. The dispatcher
// pre-computes the perfect-freehand path / alpha once and passes them
// through; brushes that don't need the path (wet) simply ignore it.

export type BrushKind = 'solid' | 'textured' | 'wet';

export interface BrushRenderArgs {
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  points: StrokePoint[];
  style: InkStyle;
  profile: BrushProfile;
  isLive: boolean;
  /** Pre-computed effective alpha (profile.baseOpacity × style.opacity ×
   *  optional pressure factor). Brushes can override but typically just
   *  apply it via `ctx.globalAlpha = args.alpha`. */
  alpha: number;
  /** Pre-computed perfect-freehand outline as a Path2D. Solid /
   *  textured brushes fill / paint this directly; the wet brush ignores
   *  it and walks the spine itself for radial-blob rendering. */
  path: Path2D;
  /** Whether the input stroke carries real (≠0.5) per-point pressure.
   *  Brushes that modulate by pressure can early-out the modulation
   *  when this is false (mouse / generic pointer). */
  hasRealPressure: boolean;
}

/** Pick the right brush from a profile. Honours `profile.kind` when
 *  set (Batch 5+ tools — explicit dispatch); falls back to the
 *  legacy flag inference for older profiles that don't declare a
 *  kind (wet > textured > solid, eraser folded into solid via its
 *  composite field). */
export function brushKindFromProfile(profile: BrushProfile): BrushKind {
  if (profile.kind) return profile.kind;
  if (profile.wetEdge) return 'wet';
  if (profile.grainBlend > 0) return 'textured';
  return 'solid';
}

// ── Ink layer ─────────────────────────────────────────────────────────────────

export class InkLayer {
  private _ctx: CanvasRenderingContext2D;

  // Committed strokes — rendered on the static layer
  private _strokes: Map<string, InkStroke> = new Map();

  // Live stroke being drawn right now
  private _liveStrokeId: string | null = null;
  private _livePoints: StrokePoint[] = [];
  private _liveStyle: InkStyle | null = null;
  /** Gold tail glow at the pen tip — defaults on, toggled by canvas
   *  via setInkTailGlow when Settings → Visual FX changes. */
  private _showInkTail = true;

  // Off-screen canvas for committed strokes (avoids full redraw every frame)
  // Sized at device pixels for crispness; internal DPR transform maps CSS coords.
  private _staticCanvas: OffscreenCanvas;
  private _staticCtx: OffscreenCanvasRenderingContext2D;
  private _staticDirty = true;
  private _dpr: number;
  private _cssWidth: number;
  private _cssHeight: number;

  // Wand-mode fading strokes (rendered per-frame, never cached)
  private _wandFades: WandFade[] = [];

  // Lava-fading strokes — selection circles that glow red before vanishing
  private _lavaFades: LavaFade[] = [];

  // Grain canvas reference for textured stroke rendering
  private _grainCanvas: OffscreenCanvas | null = null;

  /** Optional visibility filter — strokes failing the predicate are not rendered or hit-tested. */
  private _strokeFilter: ((s: InkStroke) => boolean) | null = null;

  /** Brush registry — keyed by BrushKind, values are render
   *  functions that consume a BrushRenderArgs bundle. Batch 5 added
   *  the `stylus` kinematic-aware brush; new brushes plug in here. */
  private _brushes: Record<BrushKind, (args: BrushRenderArgs) => void> = {
    solid:    (args) => this._drawSolid(args),
    textured: (args) => this._drawTextured(args),
    wet:      (args) => this._drawWet(args),
    stylus:   (args) => this._drawStylusNib(args),
  };

  /** Called every time a point is added to the live stroke (for spell detection). */
  onLivePoint: ((points: StrokePoint[]) => void) | null = null;

  constructor(ctx: CanvasRenderingContext2D, cssWidth: number, cssHeight: number, dpr: number) {
    this._ctx = ctx;
    this._dpr = dpr;
    this._cssWidth = cssWidth;
    this._cssHeight = cssHeight;
    this._staticCanvas = new OffscreenCanvas(
      Math.ceil(cssWidth * dpr), Math.ceil(cssHeight * dpr),
    );
    const staticCtx = this._staticCanvas.getContext('2d');
    if (!staticCtx) { throw new Error('Could not get OffscreenCanvas 2D context'); }
    this._staticCtx = staticCtx;
  }

  resize(cssWidth: number, cssHeight: number, dpr: number): void {
    this._dpr = dpr;
    this._cssWidth = cssWidth;
    this._cssHeight = cssHeight;
    this._staticCanvas = new OffscreenCanvas(
      Math.ceil(cssWidth * dpr), Math.ceil(cssHeight * dpr),
    );
    const staticCtx = this._staticCanvas.getContext('2d');
    if (!staticCtx) { return; }
    this._staticCtx = staticCtx;
    this._staticDirty = true;
  }

  /** Update the grain canvas reference for textured brush rendering. */
  setGrainCanvas(grain: OffscreenCanvas | null): void {
    this._grainCanvas = grain;
  }

  /** Render an arbitrary stroke (kinematic-aware) to any provided
   *  canvas context, bypassing the static-cache machinery. Used by
   *  the block_renderer to draw figure bodies through the same brush
   *  pipeline as live ink — figures with `renderPoints + inkStyle`
   *  inherit the source stroke's pressure / tilt / timing on top of
   *  the clean parametric geometry, so they render with the original
   *  brush feel rather than the legacy uniform-vector look. */
  renderShapeStroke(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    points: StrokePoint[],
    style: InkStyle,
  ): void {
    this._drawStroke(ctx, points, style, /* isLive */ false);
  }

  /**
   * Set a predicate that controls which committed strokes are rendered and
   * hit-testable.  Pass `null` to clear.  Automatically marks the static
   * cache dirty when the filter changes.
   */
  setStrokeFilter(fn: ((s: InkStroke) => boolean) | null): void {
    this._strokeFilter = fn;
    this._staticDirty = true;
  }

  // ── Stroke input ────────────────────────────────────────────────────────────

  beginStroke(id: string, style: InkStyle): void {
    this._liveStrokeId = id;
    this._livePoints = [];
    this._liveStyle = style;
  }

  addPoint(point: StrokePoint): void {
    this._livePoints.push(point);
    this.onLivePoint?.(this._livePoints);
  }

  commitStroke(): InkStroke | null {
    if (!this._liveStrokeId || !this._liveStyle || this._livePoints.length === 0) {
      return null;
    }
    const stroke: InkStroke = {
      id: this._liveStrokeId,
      points: [...this._livePoints],
      style: { ...this._liveStyle },
      timestamp: Date.now(),
    };
    this._strokes.set(stroke.id, stroke);
    this._staticDirty = true;
    this._liveStrokeId = null;
    this._livePoints = [];
    this._liveStyle = null;
    return stroke;
  }

  /**
   * Commit the live stroke as a wand stroke — it will fade out and disappear.
   * Returns the stroke (for spell detection) but never adds it to the static cache.
   */
  commitWandStroke(): InkStroke | null {
    if (!this._liveStrokeId || !this._liveStyle || this._livePoints.length === 0) {
      return null;
    }
    const stroke: InkStroke = {
      id: this._liveStrokeId,
      points: [...this._livePoints],
      style: { ...this._liveStyle },
      timestamp: Date.now(),
    };
    this._wandFades.push({ stroke, alpha: 1, startedAt: performance.now() });
    this._liveStrokeId = null;
    this._livePoints = [];
    this._liveStyle = null;
    return stroke;
  }

  cancelStroke(): void {
    this._liveStrokeId = null;
    this._livePoints = [];
    this._liveStyle = null;
  }

  addCommittedStroke(stroke: InkStroke): void {
    this._strokes.set(stroke.id, stroke);
    this._staticDirty = true;
  }

  removeStroke(id: string): boolean {
    const removed = this._strokes.delete(id);
    if (removed) { this._staticDirty = true; }
    return removed;
  }

  /**
   * Move a committed stroke into the wand-fade queue (neutral color fade-out).
   * Returns false if the stroke was not found.
   */
  addSoftFade(strokeId: string): boolean {
    const stroke = this._strokes.get(strokeId);
    if (!stroke) return false;
    this._wandFades.push({ stroke, alpha: 1, startedAt: performance.now() });
    this._strokes.delete(strokeId);
    this._staticDirty = true;
    return true;
  }

  /**
   * Overwrite the last live point's coordinates (for endpoint snapping).
   * Returns false if there are no live points.
   */
  snapEndpoint(x: number, y: number): boolean {
    if (this._livePoints.length === 0) return false;
    const last = this._livePoints[this._livePoints.length - 1];
    last.x = x;
    last.y = y;
    return true;
  }

  /**
   * Move a committed stroke into the lava-fade queue.
   * The stroke glows red-hot then dissolves over LAVA_FADE_MS.
   * Returns false if the stroke was not found.
   */
  addLavaFade(strokeId: string): boolean {
    const stroke = this._strokes.get(strokeId);
    if (!stroke) return false;
    this._lavaFades.push({
      stroke: { ...stroke, style: { ...stroke.style, color: LAVA_COLOR } },
      alpha: 1,
      startedAt: performance.now(),
    });
    this._strokes.delete(strokeId);
    this._staticDirty = true;
    return true;
  }

  clearPage(): void {
    this._strokes.clear();
    this._staticDirty = true;
  }

  /** Force a redraw of the static stroke cache (e.g. after external point transforms). */
  markDirty(): void {
    this._staticDirty = true;
  }

  /** Return a snapshot of the current committed strokes (for page caching). */
  getStrokes(): Map<string, InkStroke> {
    return new Map(this._strokes);
  }

  /** Replace all committed strokes (for restoring a cached page). */
  setStrokes(strokes: Map<string, InkStroke>): void {
    this._strokes = new Map(strokes);
    this._staticDirty = true;
  }

  // ── Rendering ───────────────────────────────────────────────────────────────

  /**
   * Draw the ink layer.  Call once per animation frame.
   * @param x  Left edge of the page in canvas coordinates
   * @param y  Top edge of the page in canvas coordinates
   */
  draw(x: number, y: number): void {
    const ctx = this._ctx;

    // Blit committed strokes from off-screen cache
    if (this._staticDirty) {
      this._redrawStatic();
      this._staticDirty = false;
    }
    // Draw at CSS size — the active DPR transform scales to device pixels,
    // giving a 1:1 mapping with the device-pixel-sized OffscreenCanvas.
    ctx.drawImage(this._staticCanvas, x, y, this._cssWidth, this._cssHeight);

    // Astral strokes — per-frame, not cached. Fade + violet aesthetic
    // until their ttl elapses (canvas_app dispatches the discard).
    this._drawAstralStrokes(ctx, x, y, Date.now());

    // Wand fading strokes (per-frame, not cached)
    const now = performance.now();
    this._wandFades = this._wandFades.filter(f => {
      f.alpha = Math.max(0, 1 - (now - f.startedAt) / WAND_FADE_MS);
      return f.alpha > 0;
    });
    if (this._wandFades.length > 0) {
      ctx.save();
      ctx.translate(x, y);
      for (const f of this._wandFades) {
        const fadedStyle = { ...f.stroke.style, opacity: f.stroke.style.opacity * f.alpha };
        this._drawStroke(ctx, f.stroke.points, fadedStyle, false);
      }
      ctx.restore();
    }

    // Lava fading strokes — glow red then dissolve
    this._lavaFades = this._lavaFades.filter(f => {
      const elapsed = now - f.startedAt;
      f.alpha = elapsed < 150 ? 1 : Math.max(0, 1 - (elapsed - 150) / (LAVA_FADE_MS - 150));
      return f.alpha > 0;
    });
    if (this._lavaFades.length > 0) {
      ctx.save();
      ctx.translate(x, y);
      for (const f of this._lavaFades) {
        ctx.save();
        if (f.alpha > 0.5) {
          ctx.shadowColor = LAVA_COLOR;
          ctx.shadowBlur = 8 * f.alpha;
        }
        const fadedStyle = { ...f.stroke.style, opacity: f.alpha };
        this._drawStroke(ctx, f.stroke.points, fadedStyle, false);
        ctx.shadowBlur = 0;
        ctx.restore();
      }
      ctx.restore();
    }

    // Draw live stroke on top (no cache — always real-time)
    if (this._livePoints.length > 0 && this._liveStyle) {
      ctx.save();
      ctx.translate(x, y);
      if (this._livePoints.length === 1) {
        // Single point: draw a pressure-sized dot so there's zero visual delay
        const p = this._livePoints[0];
        const r = this._liveStyle.size * 0.5 * (p.pressure > 0 ? p.pressure : 0.5);
        ctx.fillStyle = this._liveStyle.color;
        ctx.globalAlpha = this._liveStyle.opacity;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        this._drawStroke(ctx, this._livePoints, this._liveStyle, true);
      }
      // Ink-tail glow: the freshly-drawn segment near the pen tip
      // pulses warm gold and cools to ink over INK_TAIL_COOL_MS. A
      // simple stylus-feel detail that lives only while drawing —
      // committed strokes don't carry it.
      if (this._showInkTail) {
        this._drawInkTailGlow(ctx, now);
      }
      ctx.restore();
    }
  }

  /** Initial-tail glow on the freshly-laid head of the live stroke.
   *  Pops at pen-down and cools toward the stroke colour as the
   *  stroke progresses — like ink drying right at where the pen
   *  first touched. Confined to the first INK_TAIL_HEAD_PTS so it
   *  stays "head only" rather than chasing the pen tip. Soft satin
   *  gold-brown rather than bright gold so it blends with the page. */
  private _drawInkTailGlow(
    ctx: CanvasRenderingContext2D,
    now: number,
  ): void {
    if (!this._liveStyle || this._livePoints.length === 0) return;
    const head = this._livePoints[0];
    const ageSinceStart = now - head.timestamp;
    if (ageSinceStart > INK_TAIL_COOL_MS) return;
    const heat = 1 - ageSinceStart / INK_TAIL_COOL_MS;   // 1 = fresh, 0 = cooled

    const baseR = this._liveStyle.size * 0.7;
    const limit = Math.min(this._livePoints.length, INK_TAIL_HEAD_PTS);
    ctx.save();
    // 'lighter' blend so the glow reads on the parchment without
    // having to crank the alpha — keeps the colour feeling satin
    // rather than painted.
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < limit; i++) {
      // Each subsequent head point glows a touch less so the trail
      // feels like it's settling into ink.
      const along = 1 - i / INK_TAIL_HEAD_PTS;
      const intensity = heat * along;
      if (intensity <= 0.02) continue;
      const p = this._livePoints[i];
      const r = baseR * (1 + intensity * 0.6);
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 2.2);
      // Satin gold-brown: warm but muted, blends with parchment.
      grad.addColorStop(0,    `rgba(220, 168, 88, ${intensity * 0.70})`);
      grad.addColorStop(0.55, `rgba(176, 124, 60, ${intensity * 0.42})`);
      grad.addColorStop(1,    'rgba(80, 56, 24, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  /** Toggle the gold tail glow at the pen tip on/off (Settings →
   *  Visual FX). On by default. */
  setInkTailGlow(v: boolean): void { this._showInkTail = v; }

  private _redrawStatic(): void {
    const ctx = this._staticCtx;
    // Clear at device pixel dimensions (no transform)
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this._staticCanvas.width, this._staticCanvas.height);
    // Apply DPR transform so CSS-space stroke points map to device pixels
    ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
    const filter = this._strokeFilter;
    for (const stroke of this._strokes.values()) {
      if (filter && !filter(stroke)) continue;
      // Astral strokes are not cached — they fade per-frame and are
      // drawn dynamically in draw() so they animate even when no
      // other invalidation fires.
      if (stroke.astral) continue;
      this._drawStroke(ctx, stroke.points, stroke.style, false);
    }
  }

  /** Draw all astral strokes for the current page with their fade +
   *  aesthetic treatment. Called per-frame from draw(); not cached.
   *  Strokes whose ttl has elapsed render at alpha 0 — the canvas_app
   *  astral tick is responsible for dispatching the discard. */
  private _drawAstralStrokes(ctx: CanvasRenderingContext2D, x: number, y: number, now: number): void {
    let hasAstral = false;
    for (const s of this._strokes.values()) { if (s.astral) { hasAstral = true; break; } }
    if (!hasAstral) return;
    ctx.save();
    ctx.translate(x, y);
    for (const stroke of this._strokes.values()) {
      const ast = stroke.astral;
      if (!ast) continue;
      const elapsed = now - ast.birth;
      // Hold the aesthetic for the first 75%; fade alpha to 0 over the
      // last 25% so the look lingers before dissolving.
      const t = Math.max(0, Math.min(1, elapsed / ast.ttlMs));
      const fadeStart = 0.75;
      const fadeAlpha = t < fadeStart ? 1 : Math.max(0, 1 - (t - fadeStart) / (1 - fadeStart));
      if (fadeAlpha <= 0) continue;
      // Astral aesthetic: violet tint + soft purple halo + slight
      // shimmer via reduced opacity. Keep the original stroke geometry.
      const tintedStyle = { ...stroke.style, color: '#7c4daa', opacity: stroke.style.opacity };
      ctx.save();
      ctx.globalAlpha = fadeAlpha;
      ctx.shadowColor = 'rgba(124, 77, 170, 0.55)';
      ctx.shadowBlur = 6;
      this._drawStroke(ctx, stroke.points, tintedStyle, false);
      ctx.restore();
    }
    ctx.restore();
  }

  private _drawStroke(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    points: StrokePoint[],
    style: InkStyle,
    isLive: boolean,
  ): void {
    if (points.length < 2) { return; }

    const toolKey = style.style === 'brush' ? 'watercolor' : style.style;
    const profile = BRUSH_PROFILES[toolKey as keyof typeof BRUSH_PROFILES] ?? BRUSH_PROFILES.ink;

    // Convert to [x, y, pressure] array for Perfect Freehand
    const pfPoints = points.map(p => [p.x, p.y, tiltAwarePressure(p)] as [number, number, number]);
    const hasRealPressure = points.some(p => p.pressure > 0 && p.pressure !== 0.5);
    const outline = getStroke(pfPoints, pfOptions(style, profile, isLive, hasRealPressure));
    const path = svgPathFromOutline(outline);

    // Compute effective opacity (profile base × user opacity × pressure)
    let alpha = profile.baseOpacity * style.opacity;
    if (profile.pressureOpacity && hasRealPressure) {
      const avgPressure = points.reduce((s, p) => s + p.pressure, 0) / points.length;
      alpha *= 0.4 + 0.6 * avgPressure;
    }

    // Brush dispatch — registry lookup replaces the prior if-tree.
    // Adding a new brush only needs a new BrushKind, an entry in
    // `_brushes`, and the right brushKindFromProfile rule (or a
    // direct profile.kind override) to point at it.
    const args: BrushRenderArgs = { ctx, points, style, profile, isLive, alpha, path, hasRealPressure };
    let kind: BrushKind = brushKindFromProfile(profile);
    // Textured brush needs the grain canvas; if it isn't ready
    // (early frame before grain texture loads), fall back to solid
    // so the stroke still renders.
    if (kind === 'textured' && !this._grainCanvas) kind = 'solid';
    this._brushes[kind](args);
  }

  // ── Solid brush: Ink, Marker, Infill, Eraser ────────────────────────────
  //
  // The eraser tool also lands here — its profile carries
  // composite='destination-out', so the same fill path produces the
  // erase. Adding a tool that uses this brush is just a matter of
  // tuning the BrushProfile and pointing the Tool's brushKind here.

  private _drawSolid(args: BrushRenderArgs): void {
    const { ctx, style, profile, alpha, path } = args;
    ctx.save();
    ctx.globalCompositeOperation = profile.composite as GlobalCompositeOperation;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = style.color;
    ctx.fill(path);
    ctx.restore();
  }

  // ── Stylus / Nib brush (Batch 5) ────────────────────────────────────────
  //
  // Kinematic-aware ribbon. For each pair of consecutive points the
  // brush computes a per-side half-width from:
  //
  //   pressure  → wider at higher pressure
  //   speed     → wider as the pen slows (more pigment dwell)
  //   tilt      → biases the ribbon's two sides asymmetrically toward
  //               the leaning direction (the side of the tip in
  //               contact gets wider, the lifted side narrower)
  //
  // Two parallel polylines (left / right offsets along the local
  // perpendicular) are stitched into a closed polygon and filled.
  // Skipped fully if the stroke has fewer than two points; degenerate
  // segments (zero motion) inherit the previous segment's tangent so
  // pen wobble at pen-up doesn't explode the ribbon.

  private _drawStylusNib(args: BrushRenderArgs): void {
    const { ctx, points, style, profile, alpha } = args;
    if (points.length < 2) return;

    const baseHalfW = style.size * 0.5;
    const pressureGain = profile.nibPressureGain ?? 0.6;
    const slowGain     = profile.nibSlowGain     ?? 0.45;
    const tiltGain     = profile.nibTiltGain     ?? 0.5;
    // Speed is normalised against this px/ms scale — strokes slower
    // than ~0.4 px/ms (a relaxed pen pace) start to widen; faster
    // strokes saturate at base width.
    const SLOW_REF_PX_PER_MS = 0.4;

    type RibSide = { lx: number; ly: number; rx: number; ry: number };
    const sides: RibSide[] = [];

    let prevTx = 0, prevTy = 0;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];

      // Tangent: average of incoming + outgoing direction so the
      // ribbon doesn't kink at every sample. Endpoints fall back to
      // the single available direction.
      let tx = 0, ty = 0;
      if (i > 0) {
        const dx = p.x - points[i - 1].x;
        const dy = p.y - points[i - 1].y;
        const m = Math.hypot(dx, dy);
        if (m > 0.0001) { tx += dx / m; ty += dy / m; }
      }
      if (i < points.length - 1) {
        const dx = points[i + 1].x - p.x;
        const dy = points[i + 1].y - p.y;
        const m = Math.hypot(dx, dy);
        if (m > 0.0001) { tx += dx / m; ty += dy / m; }
      }
      const tm = Math.hypot(tx, ty);
      if (tm > 0.0001) { tx /= tm; ty /= tm; prevTx = tx; prevTy = ty; }
      else { tx = prevTx; ty = prevTy; }
      const nx = -ty;   // perpendicular (rotate 90° CCW)
      const ny = tx;

      // Per-segment speed (px/ms) derived from timestamp deltas.
      // When dt is unreliable (missing / zero / first sample), keep
      // slowness at 0 — no boost — so legacy strokes without solid
      // timing data render at base width rather than maxed out.
      let slowness = 0;
      if (i > 0) {
        const dt = p.timestamp - points[i - 1].timestamp;
        if (dt > 0) {
          const dx = p.x - points[i - 1].x;
          const dy = p.y - points[i - 1].y;
          const speed = Math.hypot(dx, dy) / dt;
          slowness = Math.max(0, 1 - speed / SLOW_REF_PX_PER_MS);
        }
      }
      const pressure = p.pressure > 0 ? p.pressure : 0.5;

      // Tilt direction (unit) and magnitude (0..1). tiltX/tiltY are
      // degrees in the page plane; their length (in degrees) gives
      // how far off-vertical the pen is.
      const tiltMagDeg = Math.hypot(p.tiltX || 0, p.tiltY || 0);
      const tiltUnitMag = Math.min(1, tiltMagDeg / 60);
      const tdx = tiltMagDeg > 0.001 ? (p.tiltX / tiltMagDeg) : 0;
      const tdy = tiltMagDeg > 0.001 ? (p.tiltY / tiltMagDeg) : 0;
      // Asymmetry: dot of tilt-direction onto stroke perpendicular.
      // Positive = pen leans toward the +n side, that side widens.
      const tiltOnPerp = tdx * nx + tdy * ny;

      // Per-point half-width composition. Base × pressure factor
      // (0.4..1+) × (1 + slowness gain). Asymmetry then redistributes
      // around the centre line.
      const widthScale = (0.4 + 0.6 * pressureGain * pressure)
                       * (1 + slowGain * slowness);
      const half = baseHalfW * widthScale;
      const asym = tiltOnPerp * tiltUnitMag * tiltGain;
      const halfL = Math.max(0.2, half * (1 + asym));
      const halfR = Math.max(0.2, half * (1 - asym));

      sides.push({
        lx: p.x + nx * halfL,
        ly: p.y + ny * halfL,
        rx: p.x - nx * halfR,
        ry: p.y - ny * halfR,
      });
    }

    ctx.save();
    ctx.globalCompositeOperation = profile.composite as GlobalCompositeOperation;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = style.color;
    ctx.beginPath();
    // Left side forward
    ctx.moveTo(sides[0].lx, sides[0].ly);
    for (let i = 1; i < sides.length; i++) {
      // Quadratic smoothing through the midpoint of consecutive samples
      // — gives the ribbon a fluid edge without jaggies at high sample
      // rates.
      const a = sides[i - 1], b = sides[i];
      const mx = (a.lx + b.lx) / 2;
      const my = (a.ly + b.ly) / 2;
      ctx.quadraticCurveTo(a.lx, a.ly, mx, my);
    }
    ctx.lineTo(sides[sides.length - 1].lx, sides[sides.length - 1].ly);
    // Cap across the end
    ctx.lineTo(sides[sides.length - 1].rx, sides[sides.length - 1].ry);
    // Right side backward
    for (let i = sides.length - 2; i >= 0; i--) {
      const a = sides[i + 1], b = sides[i];
      const mx = (a.rx + b.rx) / 2;
      const my = (a.ry + b.ry) / 2;
      ctx.quadraticCurveTo(a.rx, a.ry, mx, my);
    }
    ctx.lineTo(sides[0].rx, sides[0].ry);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // ── Textured path: Pencil, Graphite, Chalk ──────────────────────────────
  // Fill → grain knockout → edge erosion for dry-media look.
  // Edge erosion scatters small destination-out circles along the stroke
  // border so pigment breaks apart at the edges.

  private _drawTextured(args: BrushRenderArgs): void {
    const { ctx, points, style, profile, alpha, path } = args;
    const grain = this._grainCanvas!;
    const tempW = this._cssWidth;
    const tempH = this._cssHeight;

    const temp = new OffscreenCanvas(
      Math.ceil(tempW * this._dpr),
      Math.ceil(tempH * this._dpr),
    );
    const tc = temp.getContext('2d')!;
    tc.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);

    // 1) Draw the stroke fill with softness from hardness
    tc.globalAlpha = alpha;
    tc.fillStyle = style.color;

    // Soft edge glow for low-hardness tools
    if (profile.hardness < 0.9) {
      tc.shadowColor = style.color;
      tc.shadowBlur = style.size * (1 - profile.hardness) * 1.5;
    }
    // Edge noise: random shadow jitter for rough look
    if (profile.edgeNoise > 0) {
      tc.shadowColor = style.color;
      tc.shadowBlur = profile.edgeNoise * style.size * 1.2;
    }
    tc.fill(path);

    // 2) Grain knockout: punch holes where paper grain is dark
    tc.globalCompositeOperation = 'destination-out';
    tc.globalAlpha = profile.grainBlend * 0.6;
    tc.shadowColor = 'transparent';
    tc.shadowBlur = 0;
    tc.drawImage(grain, 0, 0, tempW, tempH);

    // 3) Edge erosion: scatter erase-circles along the stroke spine to
    //    break up borders, leaving vacant gaps that eat into geometry.
    if (profile.edgeErosion > 0 && points.length > 1) {
      tc.globalCompositeOperation = 'destination-out';
      tc.globalAlpha = profile.edgeErosion * 0.7;
      const r = style.size * 0.6;
      const step = Math.max(2, Math.floor(4 / profile.edgeErosion));
      let seed = ((points[0].x * 1000 + points[0].y) | 0) >>> 0;
      for (let i = 0; i < points.length; i += step) {
        const p = points[i];
        seed = ((seed * 1103515245 + 12345) & 0x7fffffff) >>> 0;
        const angle = (seed / 0x7fffffff) * Math.PI * 2;
        const dist = r * 0.5 + (((seed >> 8) & 0xff) / 255) * r * 0.7;
        const blobR = r * 0.3 + (((seed >> 16) & 0xff) / 255) * r * 0.5;
        tc.beginPath();
        tc.arc(p.x + Math.cos(angle) * dist, p.y + Math.sin(angle) * dist, blobR, 0, Math.PI * 2);
        tc.fill();
      }
    }

    // Blit the textured stroke onto the main context
    this._blitTemp(ctx, temp, tempW, tempH);
  }

  // ── Watercolor path ─────────────────────────────────────────────────────
  // Renders SDF-like radial blobs along the stroke spine that inflate
  // outward giving a watery, pigment-diffusion feel.  Edges feather
  // softly and overlap accumulates colour like real wet pigment.

  private _drawWet(args: BrushRenderArgs): void {
    const { ctx, points, style, profile, alpha } = args;
    const tempW = this._cssWidth;
    const tempH = this._cssHeight;

    const temp = new OffscreenCanvas(
      Math.ceil(tempW * this._dpr),
      Math.ceil(tempH * this._dpr),
    );
    const tc = temp.getContext('2d')!;
    tc.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);

    // Parse colour once
    const col = style.color;

    // Walk the stroke spine and place soft radial blobs at intervals.
    // Each blob is a radial gradient from colour → transparent, giving
    // a smooth SDF-like distance field instead of hard path edges.
    //
    // Batch 5: blob radius now also scales with stroke speed — slow
    // strokes pool more pigment (blobs grow), fast strokes stay at
    // base size. Implemented via the same per-segment speed
    // computation the nib brush uses.
    const baseR = style.size * 1.8;  // blob radius ~ 1.8× pen size
    const spacing = Math.max(1.5, style.size * 0.5);
    const SLOW_REF_PX_PER_MS = 0.3;
    const slowGain = 0.4;
    let travelDist = 0;
    let seed = ((points[0].x * 7919 + points[0].y * 104729) | 0) >>> 0;

    for (let i = 0; i < points.length; i++) {
      if (i > 0) {
        const dx = points[i].x - points[i - 1].x;
        const dy = points[i].y - points[i - 1].y;
        travelDist += Math.sqrt(dx * dx + dy * dy);
      }
      if (i > 0 && travelDist < spacing) continue;
      travelDist = 0;

      const p = points[i];
      const pressure = p.pressure > 0 ? p.pressure : 0.5;
      // Per-segment speed (px/ms) → slowness factor (0..1). Default
      // 0 (no boost) when timestamps are missing / zero, so legacy
      // strokes without solid timing data render at base radius.
      let slowness = 0;
      if (i > 0) {
        const dt = p.timestamp - points[i - 1].timestamp;
        if (dt > 0) {
          const dx = p.x - points[i - 1].x;
          const dy = p.y - points[i - 1].y;
          const speed = Math.sqrt(dx * dx + dy * dy) / dt;
          slowness = Math.max(0, 1 - speed / SLOW_REF_PX_PER_MS);
        }
      }
      // Blob radius varies with pressure + slowness + deterministic
      // jitter. Slow strokes get up to slowGain extra radius.
      seed = ((seed * 1103515245 + 12345) & 0x7fffffff) >>> 0;
      const rJitter = 0.7 + ((seed & 0xff) / 255) * 0.6;
      const r = baseR * pressure * rJitter * (1 + slowGain * slowness);

      // Slight positional jitter for organic feel
      seed = ((seed * 1103515245 + 12345) & 0x7fffffff) >>> 0;
      const jx = ((seed & 0xff) / 255 - 0.5) * style.size * 0.8;
      seed = ((seed * 1103515245 + 12345) & 0x7fffffff) >>> 0;
      const jy = ((seed & 0xff) / 255 - 0.5) * style.size * 0.8;

      const cx = p.x + jx;
      const cy = p.y + jy;

      // Radial gradient: soft feathered blob
      const grd = tc.createRadialGradient(cx, cy, 0, cx, cy, r);
      // Inner pigment dense, outer fades — central 40% is solid-ish
      const innerAlpha = alpha * 0.5 * pressure;
      const outerAlpha = alpha * 0.12;
      grd.addColorStop(0, col);
      grd.addColorStop(0.35, col);
      grd.addColorStop(1, 'transparent');

      tc.globalAlpha = innerAlpha;
      tc.fillStyle = grd;
      tc.beginPath();
      tc.arc(cx, cy, r, 0, Math.PI * 2);
      tc.fill();

      // A second, larger, very faint halo for the wet-diffusion edge
      if (r > 2) {
        const haloR = r * 1.6;
        const halo = tc.createRadialGradient(cx, cy, r * 0.6, cx, cy, haloR);
        halo.addColorStop(0, col);
        halo.addColorStop(1, 'transparent');
        tc.globalAlpha = outerAlpha;
        tc.fillStyle = halo;
        tc.beginPath();
        tc.arc(cx, cy, haloR, 0, Math.PI * 2);
        tc.fill();
      }
    }

    // Darken edges slightly — the classic watercolor "pigment pooling"
    // Re-draw the Perfect Freehand outline path as a thin darker border
    const hasRealPressure = points.some(p => p.pressure > 0 && p.pressure !== 0.5);
    const pfPoints = points.map(p => [p.x, p.y, tiltAwarePressure(p)] as [number, number, number]);
    const outline = getStroke(pfPoints, pfOptions(style, profile, false, hasRealPressure));
    const strokePath = svgPathFromOutline(outline);
    tc.globalCompositeOperation = 'source-atop';
    tc.globalAlpha = alpha * 0.35;
    tc.lineWidth = style.size * 0.25;
    tc.strokeStyle = col;
    tc.stroke(strokePath);

    // Optional grain interaction
    if (profile.grainBlend > 0 && this._grainCanvas) {
      tc.globalCompositeOperation = 'destination-out';
      tc.globalAlpha = profile.grainBlend * 0.35;
      tc.drawImage(this._grainCanvas, 0, 0, tempW, tempH);
    }

    // Blit onto main context
    this._blitTemp(ctx, temp, tempW, tempH);
  }

  // ── Temp canvas blit helper ─────────────────────────────────────────────

  private _blitTemp(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    temp: OffscreenCanvas,
    tempW: number,
    tempH: number,
  ): void {
    const isStatic = ctx !== this._ctx;
    if (isStatic) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(temp, 0, 0);
      ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
      ctx.restore();
    } else {
      ctx.save();
      ctx.drawImage(temp, 0, 0, tempW, tempH);
      ctx.restore();
    }
  }

  // ── Eraser ──────────────────────────────────────────────────────────────────

  /**
   * Returns IDs of strokes that intersect the given circle.
   * Used for stroke-eraser mode.
   */
  strokesInRadius(cx: number, cy: number, radius: number): string[] {
    const result: string[] = [];
    const filter = this._strokeFilter;
    for (const [id, stroke] of this._strokes) {
      if (filter && !filter(stroke)) continue;
      if (strokeIntersectsCircle(stroke.points, cx, cy, radius)) {
        result.push(id);
      }
    }
    return result;
  }

  /**
   * Returns IDs of strokes that have any point inside the given bounding box.
   * Used for region-erase (lasso + cross through).
   */
  strokesInRegion(bbox: { x: number; y: number; width: number; height: number }): string[] {
    const result: string[] = [];
    const right = bbox.x + bbox.width;
    const bottom = bbox.y + bbox.height;
    const filter = this._strokeFilter;
    for (const [id, stroke] of this._strokes) {
      if (filter && !filter(stroke)) continue;
      for (const p of stroke.points) {
        if (p.x >= bbox.x && p.x <= right && p.y >= bbox.y && p.y <= bottom) {
          result.push(id);
          break;
        }
      }
    }
    return result;
  }
}

// ── Geometry helper ───────────────────────────────────────────────────────────

function strokeIntersectsCircle(
  points: StrokePoint[],
  cx: number, cy: number, r: number,
): boolean {
  const r2 = r * r;
  for (const p of points) {
    const dx = p.x - cx, dy = p.y - cy;
    if (dx * dx + dy * dy <= r2) { return true; }
  }
  return false;
}
